(() => {
  const SELECTOR = '[data-installation-support]';

  function normalizeVideoUrl(rawUrl) {
    if (!rawUrl) return null;
    const value = String(rawUrl).trim();
    let match = value.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i);
    if (match) return { type: 'iframe', url: `https://www.youtube.com/embed/${match[1]}` };
    match = value.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    if (match) return { type: 'iframe', url: `https://player.vimeo.com/video/${match[1]}` };
    if (/\.(mp4|webm|ogg)(?:\?.*)?$/i.test(value)) return { type: 'video', url: value };
    if (/^https?:\/\//i.test(value)) return { type: 'iframe', url: value };
    return null;
  }

  function hydrateDynamicVideos(root) {
    root.querySelectorAll('[data-dynamic-video]').forEach((holder) => {
      if (holder.dataset.hydrated === 'true') return;
      const source = normalizeVideoUrl(holder.dataset.dynamicVideo);
      if (!source) return;
      if (source.type === 'video') {
        const video = document.createElement('video');
        video.src = source.url;
        video.controls = true;
        video.preload = 'metadata';
        holder.appendChild(video);
      } else {
        const iframe = document.createElement('iframe');
        iframe.src = source.url;
        iframe.title = 'Installation video';
        iframe.loading = 'lazy';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        holder.appendChild(iframe);
      }
      holder.dataset.hydrated = 'true';
    });
  }

  function pauseHiddenMedia(root) {
    root.querySelectorAll('[data-resource-panel][hidden] video').forEach((video) => video.pause());
    root.querySelectorAll('[data-resource-panel][hidden] iframe').forEach((frame) => {
      if (frame.src && /youtube|vimeo/.test(frame.src)) {
        const src = frame.src;
        frame.src = '';
        frame.src = src;
      }
    });
  }

  function selectModel(root, id, options = {}) {
    const card = root.querySelector(`[data-model-card][data-target="${CSS.escape(id)}"]`);
    const panel = root.querySelector(`[data-resource-panel="${CSS.escape(id)}"]`);
    if (!card || !panel) return;

    root.querySelectorAll('[data-model-card]').forEach((item) => {
      const active = item === card;
      item.classList.toggle('is-selected', active);
      item.querySelector('input[type="radio"]').checked = active;
    });
    root.querySelectorAll('[data-resource-panel]').forEach((item) => { item.hidden = item !== panel; });
    hydrateDynamicVideos(panel);
    pauseHiddenMedia(root);

    if (options.revealCard) {
      card.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }

    if (options.scroll) {
      panel.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }
  }

  function normalizeSearchValue(value) {
    return String(value || '')
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
  }

  function initModelSearch(root) {
    const box = root.querySelector('[data-model-search-box]');
    const input = box?.querySelector('[data-model-search-input]');
    const clearButton = box?.querySelector('[data-model-search-clear]');
    const panel = box?.querySelector('[data-model-search-panel]');
    const results = box?.querySelector('[data-model-search-results]');
    const status = box?.querySelector('[data-model-search-status]');
    const empty = box?.querySelector('[data-model-search-empty]');
    if (!box || !input || !clearButton || !panel || !results || !status || !empty) return;

    const records = [...root.querySelectorAll('[data-model-card]')].map((card, order) => ({
      card,
      order,
      id: card.dataset.target,
      title: card.dataset.modelTitle || card.querySelector('strong')?.textContent?.trim() || 'Product model',
      description: card.dataset.modelDescription || '',
      searchable: normalizeSearchValue(card.dataset.modelSearch || card.textContent),
    }));
    let matches = [];
    let activeIndex = -1;

    const close = () => {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    };

    const open = () => {
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    };

    const setActive = (index) => {
      const options = [...results.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      activeIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) => {
        const active = optionIndex === activeIndex;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', String(active));
      });
      const activeOption = options[activeIndex];
      input.setAttribute('aria-activedescendant', activeOption.id);
      activeOption.scrollIntoView({ block: 'nearest' });
    };

    const choose = (record) => {
      input.value = record.title;
      clearButton.hidden = false;
      close();
      selectModel(root, record.id, { revealCard: true, scroll: true });
    };

    const createResult = (record, index) => {
      const button = document.createElement('button');
      const copy = document.createElement('span');
      const title = document.createElement('strong');
      const arrow = document.createElement('span');
      button.type = 'button';
      button.className = 'is-product-search__result';
      button.id = `${input.id}-option-${index}`;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', 'false');
      title.textContent = record.title;
      copy.append(title);
      if (record.description) {
        const description = document.createElement('small');
        description.textContent = record.description;
        copy.append(description);
      }
      arrow.className = 'is-product-search__result-arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '›';
      button.append(copy, arrow);
      button.addEventListener('mouseenter', () => setActive(index));
      button.addEventListener('click', () => choose(record));
      return button;
    };

    const render = () => {
      const query = normalizeSearchValue(input.value);
      clearButton.hidden = !input.value;
      results.replaceChildren();
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;

      if (!query) {
        matches = [];
        status.textContent = '';
        empty.hidden = true;
        close();
        return;
      }

      const tokens = query.split(' ').filter(Boolean);
      const allMatches = records
        .filter((record) => tokens.every((token) => record.searchable.includes(token)))
        .sort((first, second) => {
          const firstTitle = normalizeSearchValue(first.title);
          const secondTitle = normalizeSearchValue(second.title);
          const firstScore = firstTitle === query ? 3 : firstTitle.startsWith(query) ? 2 : firstTitle.includes(query) ? 1 : 0;
          const secondScore = secondTitle === query ? 3 : secondTitle.startsWith(query) ? 2 : secondTitle.includes(query) ? 1 : 0;
          return secondScore - firstScore || first.order - second.order;
        });
      matches = allMatches.slice(0, 8);

      matches.forEach((record, index) => results.append(createResult(record, index)));
      if (allMatches.length > matches.length) {
        status.textContent = `Showing ${matches.length} of ${allMatches.length} matching products`;
      } else {
        status.textContent = allMatches.length === 1 ? '1 matching product' : `${allMatches.length} matching products`;
      }
      results.hidden = matches.length === 0;
      empty.hidden = matches.length !== 0;
      open();
    };

    input.addEventListener('input', render);
    input.addEventListener('focus', () => {
      if (normalizeSearchValue(input.value)) render();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (!matches.length || panel.hidden) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(activeIndex - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        choose(matches[activeIndex >= 0 ? activeIndex : 0]);
      }
    });
    clearButton.addEventListener('click', () => {
      input.value = '';
      render();
      input.focus();
    });
    document.addEventListener('pointerdown', (event) => {
      if (!box.contains(event.target)) close();
    });
  }

  function initCarousels(root) {
    root.querySelectorAll('[data-model-carousel]').forEach((carousel) => {
      const track = carousel.querySelector('[data-carousel-track]');
      const previous = carousel.querySelector('[data-carousel-prev]');
      const next = carousel.querySelector('[data-carousel-next]');
      if (!track || !previous || !next) return;

      const updateButtons = () => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= maxScroll - 2;
      };
      const move = (direction) => {
        const card = track.querySelector('[data-model-card]');
        const gap = parseFloat(getComputedStyle(track).gap) || 0;
        const distance = card ? card.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
        track.scrollBy({ left: distance * direction, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };

      previous.addEventListener('click', () => move(-1));
      next.addEventListener('click', () => move(1));
      track.addEventListener('scroll', updateButtons, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(updateButtons).observe(track);
      updateButtons();
    });
  }

  function init(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    root.querySelectorAll('[data-model-card] input').forEach((input) => {
      input.addEventListener('change', () => selectModel(root, input.value, { scroll: true }));
    });
    initModelSearch(root);
    initCarousels(root);
    const selected = root.querySelector('[data-model-card] input:checked');
    if (selected) selectModel(root, selected.value);
  }

  function initAll(scope = document) {
    if (scope.matches?.(SELECTOR)) init(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target.closest?.('[data-model-card]');
    const root = event.target.closest?.(SELECTOR);
    if (block && root) selectModel(root, block.dataset.target);
  });
})();
